import { EventsModalService } from "../../features/events/services/modal.service";
import { ClassData } from "../dtos/classes.type";
import { DEFAULT_CATEGORY } from "../services/default-category.service";
import { submissionService } from "../services/submission.service";
import { getDayByProperty, getValueByAgeCode } from "../utils/classes.util";
import { setPendingConversion } from "../utils/conversion.util";
import {
  LOGIN_BUTTON_LINK,
  RYB_CLASSES_DATA,
  URL_PARAMETERS,
} from "../utils/const.util";
import { loadShortcode } from "../../features/finder/services/shortcodes.service";
import {
  additionalUrlParameters,
  getPreloadClassId,
  getUrlParameterValue,
  parseRegistrationLink,
} from "../utils/url.util";
import { getEffectiveClassPrice } from "../utils/pricing.util";

function bindModalCloseHandlers(modalSelector: string) {
  $(document)
    .off("click.rybModalOverlay", modalSelector)
    .on("click.rybModalOverlay", modalSelector, function (event) {
      if (event.target === event.currentTarget) {
        closeModal();
      }
    });

  $(document)
    .off("click.rybModalClose", `${modalSelector} .close-modal`)
    .on("click.rybModalClose", `${modalSelector} .close-modal`, function (event) {
      event.preventDefault();
      event.stopPropagation();
      closeModal();
    });

  // Wire the "Back" button inside the registration form so users can return
  // to the "Already Registered / New Registration" choice.
  $(document)
    .off("click.rybModalBack", `${modalSelector} .modal-back-button`)
    .on("click.rybModalBack", `${modalSelector} .modal-back-button`, function (event) {
      event.preventDefault();
      event.stopPropagation();
      const $modal = $(`${modalSelector} .ryb-connect-modal`);
      $modal.find(".registration-form").removeClass("show-field");
      $modal.find(".modal-content").addClass("show-field");
    });
}

/**
 * Render an optional pre-computed display value into a slot. If the value is
 * empty, hide the slot via the `hidden` attribute so the modal collapses
 * gracefully when called from features that don't compute these fields
 * (finder, wizard, etc.).
 */
function setOrHide($slot: JQuery, html: string | undefined): void {
  if (html && html.trim().length) {
    $slot.removeAttr("hidden").html(html);
  } else {
    $slot.attr("hidden", "true").empty();
  }
}

export function openModal(
  cardData: ClassData,
  id: "invite-only" | "base" | "form" | null = null
) {
  const modal = id ? `.ryb-connect-overlay#${id}` : ".ryb-connect-overlay";
  const classMetadata = RYB_CLASSES_DATA.classes?.find(
    (category) => category.name === cardData[DEFAULT_CATEGORY]
  );
  const effectivePrice = getEffectiveClassPrice(cardData, classMetadata);
  $(".ryb-connect-modal .modal-title").html(cardData.category2); // it's predefined as category2 - keep it like that
  $(".ryb-connect-modal .modal-address").html(cardData.location);
  $(".ryb-connect-modal .modal-price").html(effectivePrice.toString());

  // Override the static `/monthly` placeholder with a per-event suffix when
  // the caller provides one (e.g. camps with day pricing supply "/day"). The
  // suffix is non-enumerable on ClassData; access via cast.
  const computedSuffix = (cardData as any).__priceSuffix as string | undefined;
  if (typeof computedSuffix === "string") {
    $(".ryb-connect-modal .modal-price-suffix").text(computedSuffix);
  }

  $(".ryb-connect-modal .modal-min-age").html(
    getValueByAgeCode(cardData.min_age)
  );
  $(".ryb-connect-modal .modal-max-age").html(
    getValueByAgeCode(cardData.max_age)
  );

  // Hide the age separator when min == max (single age, looks weird as "5 – 5")
  const minAge = getValueByAgeCode(cardData.min_age);
  const maxAge = getValueByAgeCode(cardData.max_age);
  $(".ryb-connect-modal .modal-age-sep").css("display", minAge === maxAge ? "none" : "");
  if (minAge === maxAge) {
    $(".ryb-connect-modal .modal-max-age").hide();
  } else {
    $(".ryb-connect-modal .modal-max-age").show();
  }

  $(".ryb-connect-modal .modal-gender").html(cardData.gender);

  // Use the smart meeting-day formatter (returns "Mon – Fri" for contiguous
  // ranges, "Mon, Wed, Fri" for discontiguous, single day name otherwise).
  $(".ryb-connect-modal .modal-meeting-day").html(
    getDayByProperty(cardData.meeting_days)
  );
  $(".ryb-connect-modal .modal-start-time").html(cardData.start_time);
  $(".ryb-connect-modal .modal-end-time").html(cardData.end_time);

  // New: optional date range and price breakdown slots, populated when the
  // caller supplies pre-computed strings.
  const dateRangeLabel = (cardData as any).__dateRangeLabel as string | undefined;
  const yearRangeLabel = (cardData as any).__yearRangeLabel as string | undefined;
  const dateRangeHtml = dateRangeLabel
    ? `${dateRangeLabel}${yearRangeLabel ? ` <span class="year">${yearRangeLabel}</span>` : ""}`
    : undefined;
  setOrHide($(`${modal} .modal-date-range`), dateRangeHtml);

  const breakdownHtml = (cardData as any).__priceBreakdownHtml as string | undefined;
  setOrHide($(`${modal} .modal-price-breakdown`), breakdownHtml);

  // Reset the form view back to the choice view every time the modal opens
  // so users always see "Already Registered / New Registration" first.
  const $modalEl = $(`${modal} .ryb-connect-modal`);
  $modalEl.find(".registration-form").removeClass("show-field");
  $modalEl.find(".modal-content").addClass("show-field");

  $(modal).addClass("show");
  setModalButtonsFunctionality(cardData);
  bindModalCloseHandlers(modal);

  if (id === "invite-only") {
    $(`${modal} .ryb-connect-modal`).attr("data-category-two", cardData.category2);
    $(`${modal} .ryb-connect-modal`).attr("data-class-name", cardData.name);
    const shortcode = RYB_CLASSES_DATA?.classes?.find(
      (category) =>
        category.name === cardData[DEFAULT_CATEGORY] ||
        category.display_name === cardData[DEFAULT_CATEGORY]
    )?.invite_only_funnel_shortcode;

    if (shortcode) {
      try {
        void loadShortcode(shortcode)
          .then((result) => {
            const targetResourceId = result?.wizardId || "";
            if (targetResourceId) {
              moveWizardToModal(targetResourceId);
              return;
            }
            showInviteOnlyLoadError();
          })
          .catch((error) => {
            console.error("Failed to load invite-only shortcode.", error);
            showInviteOnlyLoadError();
          });
      } catch (error) {
        console.error("Invite-only modal initialization failed.", error);
        showInviteOnlyLoadError();
      }
    } else {
      showInviteOnlyLoadError();
    }
  }
}

export function setModalButtonsFunctionality(cardData: ClassData) {
  const triggerContactModal =
    getUrlParameterValue(URL_PARAMETERS.CONTACT) === "false";
  const classMetadata =
    RYB_CLASSES_DATA.classes?.find(
      (category) => category.name === cardData[DEFAULT_CATEGORY]
    ) || null;
  const fallbackUrl = classMetadata?.fallback_url || "";
  const effectivePrice = getEffectiveClassPrice(cardData, classMetadata);

  const classId = getPreloadClassId(cardData.online_reg_link, "preLoadClassID");
  const orgId = getPreloadClassId(cardData.online_reg_link, "id");
  $(".already-registerd-button").attr("value", classId);
  $(".already-registerd-button").attr("oid", orgId);
  $(".already-registered-button")
    .data("classId", classId)
    .data("orgId", orgId)
    .attr("data-class-id", classId)
    .attr("data-org-id", orgId);

  $(".already-registered-button").off("click").on("click", function () {
    const clickedClassId = $(this).data("classId");
    const clickedOrgId = $(this).data("orgId");
    setPendingConversion({
      submissionId: submissionService.getSubmissionId(),
      className: cardData.category2 || cardData.name,
      classId: String(clickedClassId || cardData.id),
      selectionType: "enroll",
      funnelType: "finder",
      ghlContactId: localStorage.getItem("ghlContactId"),
    });
    if (triggerContactModal) {
      window.open(additionalUrlParameters(fallbackUrl || ""), "_blank");
    } else {
      window.open(
        `${LOGIN_BUTTON_LINK}?orgId=${clickedOrgId}&regcid=${clickedClassId}`,
        "_blank"
      );
    }
  });

  $("#form.ryb-connect-overlay form .form-submit")
    .data(
      "dataUrl",
      parseRegistrationLink(
        cardData.online_reg_link || fallbackUrl,
        effectivePrice.toString(),
        cardData.category1,
        cardData.category2
      )
    )
    .attr(
      "data-url",
      parseRegistrationLink(
        cardData.online_reg_link || fallbackUrl,
        effectivePrice.toString(),
        cardData.category1,
        cardData.category2
      )
    );

  $("#form.ryb-connect-overlay form .form-submit").off("click").on(
    "click",
    function (event) {
      event.preventDefault();

      const submitButton = $(this);

      // Run validation synchronously *first* so we don't open a tab unless
      // the form is valid. EventsModalService.preValidate exposes the
      // existing validateFormData logic without triggering the async submit.
      if (!EventsModalService.preValidate("#form")) {
        return;
      }

      // Open the destination tab synchronously, inside the user-gesture
      // window. Browsers block window.open if it's called after async work,
      // so we open about:blank now and navigate it once handleSubmit
      // resolves. Same pattern we used for the custom funnel.
      const newTab = window.open("about:blank", "_blank");

      submitButton
        .prop("disabled", true)
        .addClass("loading")
        .text("Processing...");

      const navigateNewTab = (url: string) => {
        if (newTab && !newTab.closed) {
          newTab.location.href = url;
        } else {
          // Popup was blocked or closed — fall back to a same-tab open
          // (the user explicitly clicked submit, so this is expected).
          window.open(url, "_blank");
        }
      };

      EventsModalService.handleSubmit(
        cardData,
        triggerContactModal
          ? additionalUrlParameters(
              fallbackUrl,
              effectivePrice.toString(),
              cardData.category1,
              cardData.category2
            )
          : parseRegistrationLink(
              cardData.online_reg_link,
              effectivePrice.toString(),
              cardData.category1,
              cardData.category2
            ),
        "#form"
      )
        ?.then((result) => {
          // handleSubmit returns { success: false } when validation failed
          // (defense-in-depth — preValidate above should already have
          // bailed). Close the empty tab to avoid leaving an "about:blank".
          if (result && result.success === false) {
            if (newTab && !newTab.closed) newTab.close();
            submitButton
              .prop("disabled", false)
              .removeClass("loading")
              .text("Continue");
            return;
          }

          setPendingConversion({
            submissionId: submissionService.getSubmissionId(),
            className: cardData.category2 || cardData.name,
            classId: String(cardData.id),
            selectionType: "enroll",
            funnelType: "events",
            ghlContactId: localStorage.getItem("ghlContactId"),
          });

          // Re-build the URL now that the rybUser cookie has been written
          // by handleSubmit, so the Jackrabbit pre-fill picks up the
          // freshly typed name/email/phone (not stale cookie data).
          const targetUrl = triggerContactModal
            ? additionalUrlParameters(
                fallbackUrl || "",
                effectivePrice.toString(),
                cardData.category1,
                cardData.category2
              )
            : parseRegistrationLink(
                cardData.online_reg_link,
                effectivePrice.toString(),
                cardData.category1,
                cardData.category2
              );
          navigateNewTab(targetUrl);

          submitButton
            .prop("disabled", false)
            .removeClass("loading")
            .text("Continue");

          $(".ryb-connect-overlay form")?.trigger("reset");

          closeModal();
        })
        .catch((error) => {
          console.error(error);
          if (newTab && !newTab.closed) newTab.close();
          submitButton
            .prop("disabled", false)
            .removeClass("loading")
            .text("Continue");
        });
    }
  );
}

export function closeModal() {
  $(".ryb-connect-overlay").removeClass("show");
  $(".ryb-connect-modal .form").empty();
  const url = new URL(window.location.href);
  url.searchParams.delete("step_id");

  window.history.replaceState({}, "", url.toString());

  const styleTags = document.head.querySelectorAll("style");
  styleTags.forEach((style) => {
    if (
      style.innerText.includes(
        "/*! tailwindcss v4.1.11 | MIT License | https://tailwindcss.com */"
      )
    ) {
      style.remove();
    }
  });
}

function showInviteOnlyLoadError() {
  const modalFormContainer = $("#invite-only .ryb-connect-modal .form");
  modalFormContainer.empty();
  modalFormContainer.html(
    "<p>Unable to load invitation form right now. Please try again.</p>"
  );
}

function moveWizardToModal(resourceId: string) {
  const modalFormContainer = $("#invite-only .ryb-connect-modal .form");
  const storedData = (window as any).theShortcodeApp?.[resourceId];
  modalFormContainer.empty();

  if (storedData) {
    modalFormContainer.html(storedData.html);
    const wizardElement = modalFormContainer.find("#ryb-wizard");
    if (wizardElement.length) {
      wizardElement.attr("data-resource-id", resourceId);

      if (typeof (window as any).renderWizardApp === "function") {
        (window as any).renderWizardApp();
      }
    } else {
      console.error("#ryb-wizard element not found in the preloaded HTML.");
      showInviteOnlyLoadError();
    }
  } else {
    console.error(`No preloaded data found for resourceId="${resourceId}"`);
    showInviteOnlyLoadError();
  }
}
